import { useMutation, useQueryClient } from '@tanstack/react-query';
import { equipamentoService } from '../../services/equipamentoService';
import { movimentacaoService } from '../../services/movimentacaoService';
import type { MovimentacaoItemExecucao, MovimentacaoItemIntake } from '../../models/movimentacao.types';
import { SI_EQUIPAMENTOS_QUERY_KEYS } from './useEquipamentoQuery';
import { SI_MOVIMENTACOES_QUERY_KEYS } from './useMovimentacoesQuery';
import { useToast } from '@/hooks/useToast';

export interface VincularEquipamentoInput {
  movimentacaoId: string;
  /** Array de execução da Movimentação, como está agora (a Movimentação pode ter mais
   * de um item — só o de `itemId` é substituído/adicionado, os demais são mantidos). */
  equipamentosExecucao: MovimentacaoItemExecucao[];
  itemId: string;
  patrimonio: string;
  nSerie?: string | null;
  modelo?: string | null;
  tipo: string;
  glpiItemId?: string | null;
  glpiItemType?: string | null;
  /** Só vêm preenchidos quando o "Buscar no GLPI" encontrou um Computer — Monitor/Phone
   * não têm esses componentes no GLPI. */
  processador?: string | null;
  memoria?: string | null;
  tamanhoDisco?: string | null;
  /** Pra copiar a propriedade do ativo (Raro/cliente) pro item — é o que o Termo usa pra saber
   * a empresa proprietária. Sem isso, o vínculo não mexe na propriedade. */
  equipamentosIntake?: MovimentacaoItemIntake[];
}

/**
 * Acha (por patrimônio) ou cria o Equipamento e vincula a um item específico dentro de
 * `equipamentosExecucao` (a Movimentação pode ter mais de um equipamento — ex. notebook
 * + monitor no mesmo envio). A partir daqui, a Cloud Function
 * onMovimentacaoWriteSyncEquipamentoCustody já existente assume a custódia
 * automaticamente (pra cada equipamentoId presente) quando a Movimentação for "entregue".
 */
export function useVincularEquipamentoMutation(solicitacaoId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (input: VincularEquipamentoInput) => {
      const existente = await equipamentoService.getByPatrimonio(input.patrimonio);
      let equipamentoId: string;
      if (existente) {
        equipamentoId = existente.id;
        // Enriquece um Equipamento já cadastrado com dados que acabaram de vir do GLPI
        // (ex. processador/memória/disco ainda não tinham como existir antes desse campo).
        if (input.processador || input.memoria || input.tamanhoDisco) {
          await equipamentoService.update({
            id: existente.id,
            processador: input.processador ?? null,
            memoria: input.memoria ?? null,
            tamanhoDisco: input.tamanhoDisco ?? null,
          });
        }
      } else {
        equipamentoId = await equipamentoService.create({
          patrimonio: input.patrimonio,
          nSerie: input.nSerie ?? null,
          modelo: input.modelo ?? null,
          tipo: input.tipo,
          // Nasce em estoque: quem dá a Situação é o mapa automático da Movimentação
          // (onMovimentacaoWriteSyncEquipamentoCustody), logo em seguida ao vínculo — antes
          // nascia "Em trânsito" e ficava assim até a entrega (ex.: RLCL063 numa devolução).
          statusCustodia: 'estoque_raro',
          glpiItemId: input.glpiItemId ?? null,
          glpiItemType: input.glpiItemType ?? null,
          processador: input.processador ?? null,
          memoria: input.memoria ?? null,
          tamanhoDisco: input.tamanhoDisco ?? null,
        });
      }

      const jaTinhaEntrada = input.equipamentosExecucao.some((item) => item.id === input.itemId);
      const equipamentosExecucaoAtualizados = jaTinhaEntrada
        ? input.equipamentosExecucao.map((item) =>
            item.id === input.itemId
              ? {
                  ...item,
                  equipamentoId,
                  patrimonio: input.patrimonio,
                  nSerie: input.nSerie ?? null,
                  modelo: input.modelo ?? null,
                }
              : item
          )
        : [
            ...input.equipamentosExecucao,
            {
              id: input.itemId,
              equipamentoId,
              patrimonio: input.patrimonio,
              nSerie: input.nSerie ?? null,
              modelo: input.modelo ?? null,
            },
          ];

      // Propriedade vem do Inventário (Raro ou do cliente) — o SI não precisa informar de novo
      // e o Termo não sai com a empresa errada. Ativo sem propriedade cadastrada: fica como estava.
      const propriedadeDoAtivo = existente?.propriedade ?? null;
      const equipamentosIntakeAtualizados =
        propriedadeDoAtivo && input.equipamentosIntake
          ? input.equipamentosIntake.map((item) =>
              item.id === input.itemId ? { ...item, propriedadeEquipamento: propriedadeDoAtivo } : item
            )
          : undefined;

      await movimentacaoService.update(solicitacaoId, {
        id: input.movimentacaoId,
        equipamentosExecucao: equipamentosExecucaoAtualizados,
        ...(equipamentosIntakeAtualizados ? { equipamentosIntake: equipamentosIntakeAtualizados } : {}),
      });

      return equipamentoId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SI_EQUIPAMENTOS_QUERY_KEYS.all });
      queryClient.invalidateQueries({ queryKey: SI_MOVIMENTACOES_QUERY_KEYS.porSolicitacao(solicitacaoId) });
    },
    onError: (error: any) => {
      toast.error(`Erro ao vincular equipamento: ${error?.message || 'erro desconhecido'}`);
    },
  });
}
